import React, { Suspense } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./auth";
import { UniversalSearchProvider } from "./context/UniversalSearchContext";
import { SessionManager } from "./components/common/SessionManager";
import GlobalSocketNotifications from "./components/common/GlobalSocketNotifications";
import AmeyoCallingPanel from "./components/calling/AmeyoCallingPanel";
import { ExportProgressProvider } from "./context/ExportProgressContext";
import ExportProgressHost from "./components/common/ExportProgressHost/ExportProgressHost";
import {
  UniversalSearchShortcutHandler,
  UniversalSearchShortcutHint,
} from "./components/common/UniversalSearchShortcutHandler";
import LoginPage from "./components/LoginPages/LoginPage";
import TwoFactorAuth from "./components/TwoFactorAuth/TwoFactorAuth";
import ForgotMailPage from "./components/ForgotMail/ForgotMailPage";
import ResetPasswordPage from "./components/ResetPassword/ResetPasswordPage";
import FirstVerifyPage from "./components/FirstVerify/FirstVerifyPage";
import EmailVerificationPage from "./components/EmailVerification/EmailVerificationPage";
import PermissionProtectedRoute from "./components/common/PermissionProtectedRoute";
import { LazyProtectedPage } from "./Routes/LazyProtectedPage";
import { RouteSuspenseFallback } from "./Routes/RouteSuspenseFallback";
import { ROUTE_LOADING_MESSAGES } from "./components/common/CenteredPageLoader/loadingMessages";
import {
  LazyAgentDashboard,
  LazyDashboardShell,
  LazyHomeDashboard,
  LazyMyLeads,
  LazyOverviewPage,
  LazyLeadDetailsPage,
  LazyConfigurationRoutes,
  LazySPOCTasksPage,
  LazySPOCTaskDetailPage,
  LazyOpportunityRedirect,
} from "./Routes/lazyPages";

/** Redirects legacy /leadDetailsPage/:id to /leadDetailsPage/patient/:id for backward compatibility */
function RedirectLeadDetailsToPatient() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/leadDetailsPage/patient/${id ?? ""}`} replace />;
}

/** Backend OAuth callback redirects here; forward query (ref / error) to the Meta integration screen. */
function DigitalLeadsConnectRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`/configurations/integration/facebook${location.search}`}
      replace
    />
  );
}

function App() {
  return (
    <div
      style={{
        height: "100vh",
        overflow: "auto",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <UniversalSearchProvider>
            <ExportProgressProvider>
              <SessionManager />
              <GlobalSocketNotifications />
              {/* Global Ameyo toolbar — Lead Details / My Leads / Overview are outside DashboardShell */}
              <AmeyoCallingPanel />
              <ExportProgressHost />
              <UniversalSearchShortcutHandler />
              <UniversalSearchShortcutHint />
              <Routes>
                <Route path="/" element={<LoginPage />} />
                <Route path="/two-factor-auth" element={<TwoFactorAuth />} />
                <Route path="/forgot-password" element={<ForgotMailPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/first-verify" element={<FirstVerifyPage />} />
                <Route
                  path="/email-verification"
                  element={<EmailVerificationPage />}
                />
                <Route
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.homeDashboard}
                      fallbackId="lazy-dashboard-shell"
                    >
                      <LazyDashboardShell />
                    </LazyProtectedPage>
                  }
                >
                  <Route
                    path="/home"
                    element={
                      <Suspense
                        fallback={
                          <RouteSuspenseFallback
                            message={ROUTE_LOADING_MESSAGES.homeDashboard}
                            id="lazy-home-dashboard"
                          />
                        }
                      >
                        <LazyHomeDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/team-dashboard"
                    element={
                      <Suspense
                        fallback={
                          <RouteSuspenseFallback
                            message={ROUTE_LOADING_MESSAGES.homeDashboard}
                            id="lazy-team-dashboard"
                          />
                        }
                      >
                        <LazyHomeDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/agent-dashboard"
                    element={
                      <Suspense
                        fallback={
                          <RouteSuspenseFallback
                            message={ROUTE_LOADING_MESSAGES.agentDashboard}
                            id="lazy-agent-dashboard"
                          />
                        }
                      >
                        <LazyAgentDashboard />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="/my-leads"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.myLeads}
                      fallbackId="lazy-my-leads"
                    >
                      <LazyMyLeads />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/overview"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.overview}
                      fallbackId="lazy-overview"
                    >
                      <LazyOverviewPage />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/leadDetailsPage/opportunity/:opportunityId"
                  element={
                    <LazyProtectedPage
                      loadingMessage={
                        ROUTE_LOADING_MESSAGES.opportunityRedirect
                      }
                      fallbackId="lazy-opportunity-redirect"
                    >
                      <LazyOpportunityRedirect />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/leadDetailsPage/:entityType/:id"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.leadDetails}
                      fallbackId="lazy-lead-details"
                    >
                      <LazyLeadDetailsPage />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/leadDetailsPage/:id"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.leadDetails}
                      fallbackId="lazy-lead-details-legacy"
                    >
                      <RedirectLeadDetailsToPatient />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/configurations/*"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.configuration}
                      fallbackId="lazy-configuration"
                    >
                      <PermissionProtectedRoute>
                        <LazyConfigurationRoutes />
                      </PermissionProtectedRoute>
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/admin/digital-leads/connect"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.configuration}
                      fallbackId="lazy-digital-leads-connect"
                    >
                      <DigitalLeadsConnectRedirect />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/spoc-tasks"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.spocTasks}
                      fallbackId="lazy-spoc-tasks"
                    >
                      <LazySPOCTasksPage />
                    </LazyProtectedPage>
                  }
                />
                <Route
                  path="/spoc-tasks/:taskId"
                  element={
                    <LazyProtectedPage
                      loadingMessage={ROUTE_LOADING_MESSAGES.spocTaskDetail}
                      fallbackId="lazy-spoc-task-detail"
                    >
                      <LazySPOCTaskDetailPage />
                    </LazyProtectedPage>
                  }
                />
              </Routes>
            </ExportProgressProvider>
          </UniversalSearchProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        closeButton
        // richColors
        expand={false}
        gap={8}
        toastOptions={{
          style: {
            fontFamily: "'Outfit', sans-serif",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.10)",
            border: "1px solid rgba(0,0,0,0.07)",
          },
          duration: 4000,
        }}
      />
    </div>
  );
}

export default App;
